import { Injector, inject } from '@angular/core';
import { provideNamedApollo } from 'apollo-angular';
import { createPersistedQueryLink } from 'apollo-angular/persisted-queries';
import { HttpLink } from 'apollo-angular/http';
import { ErrorHandlerService } from '@services/error/error.service';
import { environment } from '@/environments/environment';
import { onError } from '@apollo/client/link/error';
import { defaultDataIdFromObject, InMemoryCache } from '@apollo/client/cache';
import { uniqBy } from 'lodash-es';
import { ApolloLink, split } from '@apollo/client/core';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { DebugService } from '@core/services/debug/debug.service';
import { HttpHeaders } from '@angular/common/http';

const HEADER_INIT = new HttpHeaders();
HEADER_INIT.append('Content-Type', 'application/json');
HEADER_INIT.append('Apollo-Require-Preflight', 'true');

/**
 * Converte http(s)://… → ws(s)://… preservando porta/path. NestJS expõe o
 * endpoint `graphql-ws` no mesmo path do GraphQL HTTP — só muda o scheme.
 */
function toWsUri(uri: string): string {
  return uri.replace(/^http/i, 'ws');
}

export const provideApollo = () => {
  return provideNamedApollo(() => {
    const httpLink = inject(HttpLink);
    const errorHandlerService = inject(ErrorHandlerService);
    const debugService = inject(DebugService);
    /**
     * AuthService depende do Apollo (root-providedIn), e o Apollo factory
     * precisa do token do user pra subscriptions WS — circular se injetado
     * direto. Resolvemos via Injector lazy: a cada (re)conexão WS,
     * `connectionParams` chama `injector.get(...)` que pega a instância já
     * construída sem participar da criação do Apollo.
     */
    const injector = inject(Injector);

    const isBradesco = window.location.hostname.includes('bradesco');

    let persistedQueriesLink: ApolloLink;
    const isPersistedQueriesEnabled =
      debugService.getSingleDebugMetadata<boolean>('persistedQueriesEnabled');
    if (
      window.isSecureContext &&
      environment.ENVIRONMENT !== 'development' &&
      isPersistedQueriesEnabled !== false
    ) {
      persistedQueriesLink = createPersistedQueryLink({
        sha256: async (query) => {
          const msgUint8 = new TextEncoder().encode(query);
          const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
          return hashHex;
        },
      });
    } else {
      persistedQueriesLink = new ApolloLink((operation, forward) => forward(operation));
    }

    const httpUri = isBradesco ? environment.API_BRADESCO_URL : environment.ISENGARD_URL;
    const http = httpLink.create({ uri: httpUri, headers: HEADER_INIT });

    const cachedhttp =
      environment.ENVIRONMENT === 'development' || window.location.hostname === 'localhost'
        ? http
        : httpLink.create({
            uri: new URL('/api/graphql', window.location.origin).toString(),
            useGETForQueries: true,
            headers: HEADER_INIT,
          });

    /**
     * `graphql-ws` link para subscriptions (server NestJS configurado com
     * `subscriptions: { 'graphql-ws': true }`). Reconecta automaticamente em
     * queda de conexão; `connectionParams` é re-avaliado a cada conexão e
     * pega o token atual via AuthService — assim funciona após login lateral.
     */
    const wsLink = new GraphQLWsLink(
      createClient({
        url: toWsUri(httpUri),
        connectionParams: async () => {
          // Import dinâmico do AuthService pra quebrar o ciclo de DI no
          // bootstrap. Quando o WS efetivamente conecta, AuthService já está
          // instanciado e podemos resolvê-lo via `injector.get(...)`.
          const { AuthService } = await import('@core/services/auth/auth.service');
          const token = injector.get(AuthService).authenticatedUser()?.token;
          return token ? { Authorization: token } : {};
        },
        // Reconecta com backoff exponencial até 10s. Limitado a 10 tentativas
        // para que falhas legítimas (token revogado, manutenção do servidor,
        // rede caída por longo período) propaguem para a UI em vez de
        // mascararem com retry infinito.
        retryAttempts: 10,
        shouldRetry: () => true,
        retryWait: async (retries) =>
          new Promise((resolve) => setTimeout(resolve, Math.min(1_000 * 2 ** retries, 10_000))),
      }),
    );

    const errorLink = onError((data) => {
      errorHandlerService.errorEmitter$.next(data);
    });

    /**
     * Routing: subscriptions vão pelo WS link, queries/mutations pelo HTTP.
     * Evita o bug anterior em que `subscription { ... }` era enviada como POST
     * HTTP, ficava `(pending)` indefinidamente e esgotava o pool de 6 conexões
     * do navegador (HTTP/1.1).
     */
    const httpChain = persistedQueriesLink.concat(errorLink).concat(http);
    const link = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      wsLink,
      httpChain,
    );

    const cachedLink = errorLink.concat(cachedhttp);

    return {
      default: {
        cache: new InMemoryCache({
          dataIdFromObject: (object) => {
            switch (object.__typename) {
              case 'PaginationByOffsetUnopenedCards':
                return `${object.__typename}:${object['suid']}`;
              default:
                return defaultDataIdFromObject(object);
            }
          },
          typePolicies: {
            PaginationByOffsetUnopenedCards: {
              fields: {
                items: {
                  merge(existing = [], incoming) {
                    const items = uniqBy([...existing, ...incoming], '__ref');

                    return items;
                  },
                },
              },
            },
            Stage: {
              merge(existing, incoming) {
                return Object.assign({}, existing, incoming);
              },
            },
          },
        }),
        link,
        defaultOptions: {
          query: {
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
          },
        },
      },
      cached: {
        cache: new InMemoryCache(),
        link: cachedLink,
        defaultOptions: {
          query: {
            fetchPolicy: 'cache-first',
            errorPolicy: 'all',
          },
        },
      },
    };
  });
};
